import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, MapPin, Plus, Check, AlertTriangle, X, FileText } from "lucide-react";
import { Button, Pill, PageTitle, Input, EmptyState } from "@/design/components";
import { formatHumanCurrency, formatFull, formatRelativeShort } from "@/design/format";
import { cn } from "@/design/cn";
import { useSeed, useActorId, useAnalystName } from "@/domain/hooks";
import { useFileDrop } from "@/domain/useFileDrop";
import { useStore } from "@/domain/store";
import { useOnlineStatus } from "@/domain/useOnlineStatus";
import { api, demoModeActive } from "@/domain/api";
import { normalizeSpendRequest, normalizeSpendItem, normalizeEvidence } from "@/domain/normalizeSpend";
import type { SpendRequest, SpendItem, EvidenceItem } from "@/domain/types";
import { useSubmitEvidence } from "@/domain/useMutations";
import { evaluateCapture, distanceMeters, type Nudge } from "@/domain/nudges";
import { compressImageFile } from "@/domain/imageCompression";

// A real attachment picked from the device. The object URL backs the thumbnail and is
// revoked when the item is removed or the screen unmounts, so we never leak blobs. The
// File is kept so we can persist a durable data URL on submit — an object URL dies with
// this screen, which would leave the stored proof unviewable in the review surfaces.
type Attachment = { id: string; name: string; url: string; kind: "image" | "document"; file: File };

// Read a picked file into a base64 data URL. Unlike an object URL, this survives the
// upload screen unmounting, so the persisted evidence stays viewable "in the loop".
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

type DraftItem = { description: string; amount: number; supplier: string; note: string };

export function SubmitEvidence({ lensScope = "reporter" }: { lensScope?: "reporter" | "contractor-lead" } = {}) {
  const { askId = "", workspaceId } = useParams();
  const seed = useSeed();
  const actorId = useActorId();
  const { submit: submitEv } = useSubmitEvidence();
  const updateSeed = useStore((s) => s.updateActiveSeed);
  const isOnline = useOnlineStatus();
  const simulatedOffline = useStore((s) => s.reporterOffline);
  const offline = simulatedOffline || !isOnline;
  const navigate = useNavigate();
  const analystName = useAnalystName();
  const doneTarget = lensScope === "contractor-lead" ? `/workspace/${workspaceId}/submit-evidence` : "/reporter";
  // The device's mock capture location for this session (off the Entebbe site, which is
  // exactly what Heimdall's geo-distance check is for).
  const captureGeo = { lat: 0.3476, lng: 32.5825 };

  // Demo mode keeps the whole loop in the seed store. Live (API-first) mode must
  // resolve the ask from the backend: the Inbox links here with the backend id,
  // which the seed store does not hold, so a seed-only lookup would show "Ask not
  // found". See domain/normalizeSpend for the raw-row → domain mapping.
  const demo = demoModeActive();

  const [items, setItems] = useState<DraftItem[]>([]);
  const [form, setForm] = useState<DraftItem>({ description: "", amount: 0, supplier: "", note: "" });
  const [amountStr, setAmountStr] = useState("");
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showAttachReason, setShowAttachReason] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Every object URL we mint, revoked once on unmount. Tracking in a ref (rather than a
  // [attachments] effect) avoids revoking a still-shown thumbnail when another photo is
  // added, and never touches the durable data URLs we persist on submit.
  const objectUrls = useRef<string[]>([]);

  // Revoke every object URL on unmount so picked-file blobs are not leaked.
  useEffect(() => () => objectUrls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  // Live mode: fetch the ask (plus its logged items and prior evidence) by id.
  // The backend returns raw rows; normalise them exactly as the Inbox does so the ask
  // resolves against the same id the Inbox linked with. A real useQuery (not the old
  // one-shot useEffect) - it now survives a remount within the session (in-memory
  // cache) and refetches automatically on reconnect, instead of a hard "Could not
  // load this ask" dead end with no recovery path once offline.
  const { data: remote, isLoading: loading, isError: loadError } = useQuery({
    queryKey: ["spend-request-detail", askId],
    queryFn: async () => {
      const detail = await api.getSpendRequestDetail(askId);
      // Genuinely not found (not a load failure) resolves to null, not a
      // thrown error - the render below tells the two apart (loadError vs
      // "Ask not found"), same distinction the old code made.
      if (!detail.spendRequest) return null;
      return {
        ask: normalizeSpendRequest(detail.spendRequest),
        items: detail.items.map(normalizeSpendItem),
        evidence: detail.evidence.map(normalizeEvidence),
      };
    },
    enabled: !demo && !!askId,
  });

  const ask = demo ? seed.spendRequests.find((r) => r.id === askId) : remote?.ask;
  const existing = demo ? seed.spendItems.filter((i) => i.spendRequestId === askId) : remote?.items ?? [];
  // Evidence already submitted against this ask on an earlier visit — reporters can
  // now add more, so show what's already there for context.
  const priorEvidence = demo
    ? (seed.evidence ?? []).filter((e) => e.linkedAskId === askId)
    : remote?.evidence ?? [];

  async function addFiles(files: File[]) {
    if (files.length === 0) return;
    const picked = await Promise.all(
      files.map(async (f) => {
        const isImage = f.type.startsWith("image/");
        // Compressed before it's ever previewed, queued offline, or uploaded -
        // a device camera photo can comfortably exceed the evidence-upload
        // size limit otherwise (see server/routers/approval.js's multer cap).
        // Non-image attachments (PDFs, etc.) pass through untouched.
        const file = isImage ? await compressImageFile(f) : f;
        const url = URL.createObjectURL(file);
        objectUrls.current.push(url);
        return {
          id: `att-${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          url,
          kind: (isImage ? "image" : "document") as Attachment["kind"],
          file,
        };
      }),
    );
    setAttachments((xs) => [...xs, ...picked]);
    setShowAttachReason(false);
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    // Clear the input value now (not inside addFiles, which is also called
    // from the drop handler, where there is no input element to clear) so
    // re-picking the same file (e.g. re-take) still fires a change event.
    e.target.value = "";
    void addFiles(files);
  }

  const fileDrop = useFileDrop({ onFiles: (files) => void addFiles(files) });

  function removeAttachment(id: string) {
    setAttachments((xs) => {
      const gone = xs.find((a) => a.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return xs.filter((a) => a.id !== id);
    });
  }

  if (!demo && loading) {
    return <div className="p-6"><EmptyState title="Loading ask" body="Fetching this spend ask from the database…" /></div>;
  }

  if (!ask) {
    return <div className="p-6 text-base text-mute">{loadError ? "Could not load this ask." : "Ask not found."} <button className="text-brand" onClick={() => navigate(doneTarget)}>Back</button></div>;
  }

  const project = seed.projects.find((p) => p.id === ask.projectId);
  const siteGeo = project?.lat != null && project?.lng != null ? { lat: project.lat, lng: project.lng } : undefined;
  // Heimdall capture-time guidance (advisory; never blocks submit) — AI brief §3.
  const nudges = evaluateCapture({ geo: captureGeo, siteGeo, siteName: project?.name, siteRadiusM: 500 });
  const visibleNudges = nudges.filter((n) => !dismissed.includes(n.id));

  // Human location, same distance logic the capture nudge uses: "at the site" when inside
  // the 500 m tolerance, otherwise "about N km/m from the site". Never the raw lat/lng.
  const SITE_RADIUS_M = 500;
  const geoLabel = (() => {
    if (!siteGeo) return "Location captured";
    const d = distanceMeters(captureGeo, siteGeo);
    if (d <= SITE_RADIUS_M) return "At the site";
    const km = Math.round(d / 100) / 10;
    const dist = km >= 1 ? `${km} km` : `${Math.round(d)} m`;
    return `About ${dist} from the site`;
  })();
  const geoOffSite = !!siteGeo && distanceMeters(captureGeo, siteGeo) > SITE_RADIUS_M;

  function dismissNudge(n: Nudge) {
    setDismissed((d) => [...d, n.id]);
    updateSeed((s) => ({
      ...s,
      auditEvents: [
        {
          id: `audit-nudge-${n.id}-${Date.now()}`,
          actorId,
          actorOrganisationId: ask!.organisationId,
          class: "system" as const,
          description: `${analystName} nudge dismissed: ${n.message}`,
          timestamp: new Date().toISOString(),
          scope: { projectId: ask!.projectId },
        },
        ...s.auditEvents,
      ],
    }));
  }

  // Approved-budget tracking: recorded spend — already-logged items plus this session's
  // drafts — measured against the ask's approved budget. Overspend no longer blocks the
  // reporter (§6 spend flow); it is surfaced as a soft advisory here and flagged to
  // oversight, who confirm the recorded spend.
  const approvedBudget = ask.amount;
  const loggedTotal = existing.reduce((s, i) => s + i.amount, 0);
  const draftTotal = items.reduce((s, i) => s + i.amount, 0);
  const remainingBudget = approvedBudget - loggedTotal - draftTotal;
  const overBudget = remainingBudget < 0;

  function addItem() {
    if (!form.description.trim() || !amountStr) return;
    const amount = Number(amountStr.replace(/[^0-9]/g, ""));
    // Any amount is allowed — overspend is advisory, not blocked. The budget breakdown and
    // the pre-submit advisory show the reporter when recorded spend is over budget.
    setItems((xs) => [...xs, { ...form, amount }]);
    setForm({ description: "", amount: 0, supplier: "", note: "" });
    setAmountStr("");
  }

  // Submit is blocked only until the reporter has attached at least one real photo or
  // receipt; the inline reason explains why rather than silently disabling the button.
  // Overspend does not block — it is submitted and flagged to oversight.
  const canSend = items.length > 0 && attachments.length > 0;
  const primaryAttachment = attachments[0];

  async function send() {
    if (attachments.length === 0) {
      setShowAttachReason(true);
      return;
    }
    const geo = captureGeo;
    const now = new Date().toISOString();
    // Persist a durable data URL PER attachment, not the object URL — the latter is
    // revoked when this screen unmounts, which would blank the image in every review
    // surface (EvidenceModal, SpendAskDrawer, the approval queues). Carrying the url on
    // each doc lets the contractor/oversight preview every photo or receipt inline, the
    // same way evidence raised with an ask does (§6 spend flow).
    const docs = await Promise.all(
      attachments.map(async (a) => ({
        id: `ev-${a.id}`,
        filename: a.name,
        kind:
          a.kind === "image"
            ? ("image" as const)
            : a.file.type === "application/pdf" || a.name.toLowerCase().endsWith(".pdf")
              ? ("pdf" as const)
              : ("doc" as const),
        sizeBytes: a.file.size ?? 0,
        uploadedAt: now,
        uploadedById: actorId,
        url: await readAsDataUrl(a.file),
      })),
    );
    const primaryUrl = docs[0]?.url;
    await submitEv({
      askId,
      spendItems: items.map((it) => ({
        description: it.description,
        amount: it.amount,
        category: ask?.category,
        supplier: it.supplier || undefined,
        note: it.note || undefined,
        evidence: docs,
        geoTag: { ...geo, capturedAt: now },
      })),
      evidence: {
        type: primaryAttachment?.kind === "image" ? "photo" : "document",
        geo,
        note: items.map((i) => i.description).join("; ") || undefined,
        url: primaryUrl,
        filename: primaryAttachment?.name,
        // The real file - uploaded for real (online) or durably queued for
        // later real upload (offline). `url` above stays base64, used only
        // for the immediate local preview.
        file: primaryAttachment?.file,
      },
    });
    // Overspend is allowed but must be durably traceable: if the recorded total now exceeds
    // the approved budget, write a spend audit event so oversight has a record of the
    // overspend, not just the live UI flag (§6 spend flow).
    const overspend = loggedTotal + draftTotal - approvedBudget;
    if (overspend > 0) {
      updateSeed((s) => ({
        ...s,
        auditEvents: [
          {
            id: `audit-overspend-${askId}-${Date.now()}`,
            actorId,
            actorOrganisationId: ask!.organisationId,
            class: "spend" as const,
            description: `Evidence submitted over approved budget on '${ask!.title}': recorded ${formatFull(loggedTotal + draftTotal)} against an approved ${formatFull(approvedBudget)} (${formatFull(overspend)} over).`,
            amount: overspend,
            timestamp: now,
            scope: { projectId: ask!.projectId, subProjectId: ask!.subProjectId },
            refId: askId,
          },
          ...s.auditEvents,
        ],
      }));
    }
    if (offline) {
      // enqueueAction (inside useSubmitEvidence) already durably queued this
      // for real replay once back online - nothing to do here but tell the
      // user.
      toast.message("Evidence saved offline. It will sync when you are back online.");
    } else {
      toast.success(lensScope === "contractor-lead" ? "Evidence submitted against the ask" : "Evidence sent to Stratus oversight");
    }
    navigate(doneTarget);
  }

  const total = items.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="p-4">
      <PageTitle title={ask.title} subtitle={seed.milestones.find((m) => m.id === ask.milestoneId)?.name} />

      {/* Budget vs itemised breakdown */}
      {(() => {
        const logged = existing.reduce((s, i) => s + i.amount, 0);
        const draft = items.reduce((s, i) => s + i.amount, 0);
        const remaining = ask.amount - logged - draft;
        return (
          <div className="mt-4 rounded-card border border-line bg-card p-4">
            <div className="flex items-center justify-between text-base">
              <span className="text-faint">Approved budget</span>
              <span className="whitespace-nowrap font-medium tabular-nums text-ink">{formatHumanCurrency(ask.amount)}</span>
            </div>
            <div className="mt-2.5 flex h-2.5 overflow-hidden rounded-full bg-vizTrack">
              <div className="h-full bg-okFill" style={{ width: `${Math.min(100, (logged / ask.amount) * 100)}%` }} />
              <div className="h-full bg-brand" style={{ width: `${Math.min(100, (draft / ask.amount) * 100)}%` }} />
            </div>
            <div className="mt-2.5 flex items-center justify-between text-sm text-faint">
              <span>Logged {formatHumanCurrency(logged)}</span>
              <span>To send {formatHumanCurrency(draft)}</span>
              <span className={remaining < 0 ? "text-badStrong" : "text-mute"}>Remaining {formatHumanCurrency(Math.max(0, remaining))}</span>
            </div>
          </div>
        );
      })()}

      {existing.length > 0 && (
        <div className="mt-5">
          <p className="text-meta mb-2 text-faint">Already logged</p>
          <ul className="space-y-2">
            {existing.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-3 rounded-control border border-line bg-card px-3 py-2.5 text-base">
                <span className="text-ink">{i.description}</span>
                <span className="whitespace-nowrap tabular-nums text-mute">{formatHumanCurrency(i.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {priorEvidence.length > 0 && (
        <div className="mt-5">
          <p className="text-meta mb-2 text-faint">Evidence already submitted ({priorEvidence.length})</p>
          <ul className="space-y-2">
            {priorEvidence.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 rounded-control border border-line bg-card px-3 py-2.5 text-base">
                <span className="flex items-center gap-2 text-ink"><FileText className="h-4 w-4 shrink-0 text-faint" aria-hidden />{e.filename ?? e.type}</span>
                <span className="whitespace-nowrap text-sm text-faint">{formatRelativeShort(e.capturedAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-5 flex items-center justify-between rounded-control bg-subtle px-3 py-2.5 text-base">
          <span className="text-mute">{items.length} item{items.length > 1 ? "s" : ""} to send</span>
          <span className="font-medium tabular-nums text-ink">{formatHumanCurrency(total)}</span>
        </div>
      )}

      <ul className="mt-3 space-y-2">
        {items.map((i, idx) => (
          <li key={idx} className="rounded-card border border-line bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-base font-medium text-ink">{i.description}</span>
              <span className="text-base tabular-nums text-ink">{formatHumanCurrency(i.amount)}</span>
            </div>
            <p className="mt-1 text-sm text-faint">{i.supplier}</p>
          </li>
        ))}
      </ul>

      {/* What good evidence looks like — a light, collapsible hint. */}
      <details className="mt-5 rounded-card border border-line bg-card p-4">
        <summary className="cursor-pointer text-base font-medium text-faint">What good evidence looks like</summary>
        <ul className="mt-2.5 space-y-1.5 text-sm text-mute">
          <li>· A clear, well-lit photo of the delivery or completed work</li>
          <li>· The receipt or delivery note, with the supplier and amount legible</li>
          <li>· Captured on site so the location tag matches the project</li>
        </ul>
      </details>

      {/* Heimdall capture-time nudges — dismissible, never blocking (P2.1) */}
      {visibleNudges.length > 0 && (
        <div className="mt-5 space-y-2">
          {visibleNudges.map((n) => (
            <div
              key={n.id}
              className={`flex items-start gap-2.5 rounded-card border px-3 py-2.5 ${n.severity === "attention" ? "border-warn bg-warnSoft" : "border-info bg-infoSoft"}`}
            >
              <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${n.severity === "attention" ? "text-warn" : "text-info"}`} aria-hidden />
              <p className={`flex-1 text-sm ${n.severity === "attention" ? "text-warnStrong" : "text-infoStrong"}`}>{n.message}</p>
              <button type="button" onClick={() => dismissNudge(n)} aria-label="Dismiss" className="focus-ring -m-2.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full p-0.5 text-faint hover:text-mute">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {/* TODO(heimdall): restore the "{analyst} guidance" attribution when the AI analyst ships. */}
        </div>
      )}

      {/* Photo / receipt capture — a real file picker (camera on mobile), or drag and drop. */}
      <div
        onDragOver={fileDrop.onDragOver}
        onDragLeave={fileDrop.onDragLeave}
        onDrop={fileDrop.onDrop}
        className={cn(
          "mt-3 space-y-3 rounded-card border p-4 transition-colors",
          fileDrop.isDraggingOver ? "border-brand bg-brandSoft" : "border-line bg-card",
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-base font-medium text-ink">Photos and receipts</p>
          {attachments.length > 0 && (
            <span className="text-sm tabular-nums text-mute">{attachments.length} attached</span>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          multiple
          className="hidden"
          onChange={onPickFiles}
        />

        {attachments.length > 0 && (
          <ul className="grid grid-cols-3 gap-2.5">
            {attachments.map((a) => (
              <li key={a.id} className="relative">
                {a.kind === "image" ? (
                  <img src={a.url} alt={a.name} className="h-24 w-full rounded-control border border-line object-cover" />
                ) : (
                  <div className="flex h-24 w-full flex-col items-center justify-center gap-1.5 rounded-control border border-line bg-subtle px-2 text-center">
                    <FileText className="h-6 w-6 text-faint" aria-hidden />
                    <span className="line-clamp-2 break-all text-meta text-mute">{a.name}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(a.id)}
                  aria-label={`Remove ${a.name}`}
                  className="focus-ring absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-full text-mute hover:text-ink"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-card/90 shadow-overlay">
                    <X className="h-3.5 w-3.5" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <Button size="lg" variant="secondary" className="w-full" onClick={() => fileInputRef.current?.click()}>
          <Camera className="h-4 w-4" /> {attachments.length > 0 ? "Add another photo" : "Add photo"}
        </Button>

        <div className="flex items-center gap-2">
          <Pill variant={geoOffSite ? "warn" : "neutral"}>
            <MapPin className="h-3 w-3" /> {geoLabel}
          </Pill>
        </div>

        {showAttachReason && attachments.length === 0 && (
          <p className="flex items-start gap-2 text-sm text-warnStrong">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            Add at least one photo or receipt before you submit.
          </p>
        )}
      </div>

      {/* Add item form */}
      <div className="mt-3 space-y-3 rounded-card border border-line bg-card p-4">
        <p className="text-base font-medium text-ink">Add spend item</p>
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="py-3 text-base" />
        <Input value={amountStr} onChange={(e) => setAmountStr(e.target.value)} inputMode="numeric" placeholder="Amount" className="py-3 text-base" />
        <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Supplier" className="py-3 text-base" />
        <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note" className="py-3 text-base" />
        <Button size="lg" variant="secondary" className="w-full" onClick={addItem}><Plus className="h-4 w-4" /> Add item</Button>
      </div>

      {/* Pre-submission check: a missing attachment blocks; overspend and open nudges only
          advise, never block (§6 spend flow, P2.1). */}
      {(() => {
        const blocked = items.length === 0 || attachments.length === 0;
        const reason = items.length === 0
          ? "Add at least one spend item before submitting."
          : attachments.length === 0
            ? "Add at least one photo or receipt before submitting."
            : overBudget
            ? `Recorded spend is ${formatHumanCurrency(-remainingBudget)} over the approved budget of ${formatHumanCurrency(ask.amount)}. You can still submit — Stratus will see the overspend flagged.`
            : visibleNudges.length
            ? `${visibleNudges.length} thing${visibleNudges.length > 1 ? "s" : ""} to check before submitting`
            : "Looks ready to submit";
        const warn = blocked || overBudget || visibleNudges.length > 0;
        return (
          <div className={`mt-5 flex items-center gap-2 rounded-card border px-3 py-2.5 text-sm ${warn ? "border-warn bg-warnSoft text-warnStrong" : "border-ok bg-okSoft text-okStrong"}`}>
            {warn ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <Check className="h-4 w-4 shrink-0" />}
            {reason}
          </div>
        );
      })()}

      <Button size="lg" className="mt-3 w-full" disabled={!canSend} onClick={send}>
        <Check className="h-4 w-4" /> {priorEvidence.length > 0
          ? "Send additional evidence"
          : lensScope === "contractor-lead" ? "Submit evidence" : "Send to Stratus oversight"}
      </Button>
    </div>
  );
}
